import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { colors } from '@/theme/variables';

const PieContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: '12px',
}));

const ChartAndLegendContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
}));

const ChartContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const PieChartSvg = styled('svg')(() => ({
  // Note: polarToCartesian already handles -90deg rotation to start from top
}));

const LegendContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: 1,
  minWidth: 0,
  alignItems: 'flex-start',
}));

const LegendItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  minWidth: '140px',
}));

const LegendColor = styled(Box)<{ color: string }>(({ color }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: color,
  flexShrink: 0,
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.mode === 'dark' ? 'white' : colors.grey[70],
  fontWeight: 400,
}));

const LegendPercentage = styled(Typography)(() => ({
  fontSize: '12px',
  color: colors.grey[90],
  fontWeight: 600,
}));

const TotalContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
}));

const TotalValue = styled(Typography)(({ theme }) => ({
  fontSize: '32px',
  fontWeight: 500,
  color: theme.palette.mode === 'dark' ? 'white' : colors.grey[100],
}));

const TotalLabel = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 400,
  color: colors.grey[80],
}));

export interface PieChartData {
  type: string;
  percentage: number;
  count: number;
}

export interface PieChartCardData {
  type: 'pieChart';
  value: number;
  breakdown: PieChartData[];
}

interface PieChartWidgetProps {
  data: PieChartCardData;
  showTotal?: boolean;
}

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const PIE_COLORS = ['#A9EE8A', '#9E9E9E', '#000000'];

const createPieSlice = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  color: string,
) => {
  // Full circle: SVG arc can't draw 360°, use a circle element instead
  if (endAngle - startAngle >= 359.99) {
    return <circle cx={cx} cy={cy} r={radius} fill={color} />;
  }

  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  const d = [
    'M',
    cx,
    cy,
    'L',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
    'Z',
  ].join(' ');

  return <path d={d} fill={color} />;
};

/**
 * Renders pie chart + legend to an off-screen canvas and returns a base64 PNG.
 * Used for embedding in pdfmake PDF definitions.
 */
export const renderPieChartToBase64 = (data: PieChartCardData): string => {
  const { breakdown } = data;
  const chartSize = 140;
  const chartRadius = 60;
  const chartCx = chartSize / 2;
  const chartCy = chartSize / 2;

  const legendWidth = 160;
  const canvasWidth = chartSize + 20 + legendWidth;
  const canvasHeight = chartSize;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth * 2; // 2x for sharpness
  canvas.height = canvasHeight * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(2, 2);

  // Add gray background
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Draw pie slices counter-clockwise to match SVG rendering
  let currentAngle = -Math.PI / 2; // Start from top
  breakdown.forEach((item, index) => {
    const sliceAngle = (item.percentage / 100) * 2 * Math.PI;
    ctx.beginPath();
    ctx.moveTo(chartCx, chartCy);
    ctx.arc(
      chartCx,
      chartCy,
      chartRadius,
      currentAngle,
      currentAngle - sliceAngle,
      true, // counter-clockwise to match SVG
    );
    ctx.closePath();
    ctx.fillStyle = PIE_COLORS[index % PIE_COLORS.length];
    ctx.fill();
    currentAngle -= sliceAngle;
  });

  // Draw center circle to create doughnut effect
  ctx.beginPath();
  ctx.arc(chartCx, chartCy, chartRadius * 0.55, 0, 2 * Math.PI);
  ctx.fillStyle = 'white';
  ctx.fill();

  // Draw legend
  const legendX = chartSize + 20;
  const legendStartY = (canvasHeight - breakdown.length * 22) / 2;
  breakdown.forEach((item, index) => {
    const y = legendStartY + index * 22;
    // Color dot
    ctx.beginPath();
    ctx.arc(legendX + 5, y + 6, 5, 0, 2 * Math.PI);
    ctx.fillStyle = PIE_COLORS[index % PIE_COLORS.length];
    ctx.fill();
    // Label
    ctx.fillStyle = '#555';
    ctx.font = '12px sans-serif';
    ctx.fillText(`${item.type}`, legendX + 16, y + 10);
    // Percentage (right-aligned)
    ctx.fillStyle = '#333';
    ctx.font = 'bold 12px sans-serif';
    ctx.fillText(
      `${item.percentage.toFixed(1)}%`,
      legendX + legendWidth - 40,
      y + 10,
    );
  });

  return canvas.toDataURL('image/png');
};

export const PieChartWidget = ({
  data,
  showTotal = true,
}: PieChartWidgetProps) => {
  const { value: totalValue, breakdown } = data;
  const theme = useTheme();
  const size = 140;
  const radius = 60;
  const cx = size / 2;
  const cy = size / 2;

  let currentAngle = 0;

  return (
    <PieContainer>
      {/* Title text at the top */}
      {showTotal && totalValue > 0 && (
        <TotalContainer>
          <TotalValue theme={theme}>{totalValue.toLocaleString()}</TotalValue>
          <TotalLabel>calls/year</TotalLabel>
        </TotalContainer>
      )}

      {/* Chart and legend side by side */}
      <ChartAndLegendContainer>
        <ChartContainer>
          <PieChartSvg width={size} height={size}>
            {breakdown.map((item, index) => {
              const sliceAngle = (item.percentage / 100) * 360;
              const slice = createPieSlice(
                cx,
                cy,
                radius,
                currentAngle,
                currentAngle + sliceAngle,
                PIE_COLORS[index % PIE_COLORS.length],
              );
              currentAngle += sliceAngle;
              return <g key={item.type}>{slice}</g>;
            })}
            {/* Center circle to create doughnut effect */}
            <circle cx={cx} cy={cy} r={radius * 0.55} fill="white" />
          </PieChartSvg>
        </ChartContainer>

        <LegendContainer>
          {breakdown.map((item, index) => (
            <LegendItem key={item.type}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LegendColor color={PIE_COLORS[index % PIE_COLORS.length]} />
                <LegendText theme={theme}>{item.type}</LegendText>
              </Box>
              <LegendPercentage>{item.percentage.toFixed(1)}%</LegendPercentage>
            </LegendItem>
          ))}
        </LegendContainer>
      </ChartAndLegendContainer>
    </PieContainer>
  );
};
